import * as React from "react";

import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Textarea } from "src/components/ui/textarea";
import { cn } from "src/lib/utils";

export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Field label */
  label?: React.ReactNode;
  /** Helper text shown below the input */
  helperText?: React.ReactNode;
  /** Error state */
  error?: boolean;
  /** Error text (shown instead of helperText when error is true) */
  errorText?: string;
  /** Makes the input take full width */
  fullWidth?: boolean;
  /** Multiline mode (renders textarea) */
  multiline?: boolean;
  /** Number of rows for multiline */
  rows?: number;
  /** Min rows for multiline */
  minRows?: number;
  /** Max rows for multiline */
  maxRows?: number;
  /** MUI variant (for compatibility, maps to styling) */
  variant?: "outlined" | "filled" | "standard";
  /** Input size */
  size?: "small" | "medium";
  /** Start adornment */
  startAdornment?: React.ReactNode;
  /** End adornment */
  endAdornment?: React.ReactNode;
  /** Container className */
  containerClassName?: string;
  /** MUI slotProps compatibility (limited support) */
  slotProps?: {
    input?: {
      sx?: Record<string, unknown>;
      startAdornment?: React.ReactNode;
      endAdornment?: React.ReactNode;
      [key: string]: unknown;
    };
  };
  /** MUI InputProps compatibility */
  InputProps?: {
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    [key: string]: unknown;
  };
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      className,
      containerClassName,
      label,
      helperText,
      error,
      errorText,
      fullWidth,
      multiline,
      rows,
      minRows,
      maxRows,
      variant = "outlined",
      size = "medium",
      startAdornment,
      endAdornment,
      slotProps,
      InputProps,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || React.useId();
    const hasError = error || !!errorText;
    const displayHelperText = hasError ? errorText : helperText;

    // Extract adornments from slotProps or InputProps
    const resolvedStartAdornment =
      startAdornment ||
      slotProps?.input?.startAdornment ||
      InputProps?.startAdornment;
    const resolvedEndAdornment =
      endAdornment ||
      slotProps?.input?.endAdornment ||
      InputProps?.endAdornment;

    const inputClassName = cn(
      size === "small" && "h-7 text-xs",
      hasError && "border-destructive focus-visible:ring-destructive",
      variant === "standard" &&
        "border-0 border-b border-border rounded-none focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary bg-transparent",
      variant === "filled" &&
        "bg-surface-2 border-0 border-b border-border rounded-t-lg rounded-b-none",
      className
    );

    const inputElement = multiline ? (
      <Textarea
        id={inputId}
        className={inputClassName}
        rows={rows || minRows}
        aria-invalid={hasError}
        aria-describedby={displayHelperText ? `${inputId}-helper` : undefined}
        {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    ) : (
      <Input
        ref={ref}
        id={inputId}
        className={inputClassName}
        aria-invalid={hasError}
        aria-describedby={displayHelperText ? `${inputId}-helper` : undefined}
        {...props}
      />
    );

    return (
      <div
        className={cn(
          "flex flex-col gap-1.5",
          fullWidth && "w-full",
          containerClassName
        )}
      >
        {label && (
          <Label
            htmlFor={inputId}
            className={cn(hasError && "text-destructive")}
          >
            {label}
          </Label>
        )}
        {resolvedStartAdornment || resolvedEndAdornment ? (
          <div className="relative flex items-center">
            {resolvedStartAdornment && (
              <div className="absolute left-3 flex items-center pointer-events-none text-muted-foreground">
                {resolvedStartAdornment}
              </div>
            )}
            {React.cloneElement(inputElement as React.ReactElement, {
              className: cn(
                inputClassName,
                resolvedStartAdornment && "pl-8",
                resolvedEndAdornment && "pr-10"
              ),
            })}
            {resolvedEndAdornment && (
              <div className="absolute right-3 flex items-center text-muted-foreground">
                {resolvedEndAdornment}
              </div>
            )}
          </div>
        ) : (
          inputElement
        )}
        {displayHelperText && (
          <p
            id={`${inputId}-helper`}
            className={cn(
              "text-xs",
              hasError ? "text-destructive" : "text-text-tertiary"
            )}
          >
            {displayHelperText}
          </p>
        )}
      </div>
    );
  }
);
TextField.displayName = "TextField";

export { TextField };
